import { useEffect, useMemo } from "react";
import { useSnapshot } from "valtio";
import { set3DEmblemOption, setEmblemLeatherOption, setEmblemPrintStitchOption, wizardStore } from "../stores/wizard";
import { Steps } from "../constants";
import {
  getStepRuleConfig,
  findDefaultOption,
  StepRuleConfig,
  getUIRules,
} from "../config/productRules";
import { StepOption } from "../types/wizard";

interface UseProductRulesResult {
  stepRule: StepRuleConfig | null;
  isReadOnly: boolean;
  isHidden: boolean;
  defaultOption: StepOption | null;
  excludedOptionCodes: string[];
}

export function useProductRules(
  stepId: string,
  options?: StepOption[]
): UseProductRulesResult {
  const wizSnap = useSnapshot(wizardStore);

  const productType = useMemo(() => {
    const data = wizSnap.emblemData[Steps.ProductType];
    return data ? { code: data.code, name: data.name } : null;
  }, [wizSnap.emblemData[Steps.ProductType]]);

  const productSubtype = useMemo(() => {
    const data = wizSnap.emblemData[Steps.ProductSubtype];
    return data ? { code: data.code, name: data.name } : null;
  }, [wizSnap.emblemData[Steps.ProductSubtype]]);

  const stepRule = useMemo(() => {
    return getStepRuleConfig(stepId, productType, productSubtype);
  }, [stepId, productType, productSubtype]);

  const excludedOptionCodes = stepRule?.excludedOptionCodes ?? [];

  const defaultOption = useMemo(() => {
    if (!stepRule || !options) return null;
    return findDefaultOption<StepOption>(options, stepRule);
  }, [stepRule, options]);

  return {
    stepRule,
    isReadOnly: stepRule?.readOnly ?? false,
    isHidden: stepRule?.hidden ?? false,
    excludedOptionCodes,
    defaultOption,
  };
}

export function useIsColorSelectionDisabled(): boolean {
  const { isHidden } = useProductRules(Steps.ThreadColors);
  return isHidden;
}

export function useUIRules() {
  const wizSnap = useSnapshot(wizardStore);

  const productType = wizSnap.emblemData[Steps.ProductType] as {
    code?: string | null;
    name?: string;
  } | null;
  const productSubtype = wizSnap.emblemData[Steps.ProductSubtype] as {
    code?: string | null;
    name?: string;
  } | null;

  const uiRules = useMemo(() => {
    return getUIRules(productType, productSubtype);
  }, [productType, productSubtype]);

  return uiRules;
}

export function useApplyUIRulesEffects() {
  const wizSnap = useSnapshot(wizardStore);
  const uiRules = useUIRules();

  const productType = wizSnap.emblemData[Steps.ProductType] as {
    code?: string | null;
    name?: string;
  } | null;
  const productSubtype = wizSnap.emblemData[Steps.ProductSubtype] as {
    code?: string | null;
    name?: string;
  } | null;

  useEffect(() => {
    if (uiRules?.letStahlsChooseIndications?.defaultValue) {
      const stepPrintStitchRule = getStepRuleConfig(Steps.EmblemPrintStitch, productType, productSubtype);
      if (stepPrintStitchRule?.hidden) {
        setEmblemPrintStitchOption(Steps.EmblemPrintStitch, "AllowWEIToChoose");
      }

      const step3dRule = getStepRuleConfig(Steps.Emblem3d, productType, productSubtype);
      if (step3dRule?.hidden) {
        set3DEmblemOption(Steps.Emblem3d, "AllowWEIToChoose");
      }

      const stepLeatherRule = getStepRuleConfig(Steps.EmblemLeather, productType, productSubtype);
      if (stepLeatherRule?.hidden) {
        setEmblemLeatherOption(Steps.EmblemLeather, "AllowWEIToChoose");
      }
    }
  }, [uiRules?.letStahlsChooseIndications?.defaultValue, productType, productSubtype]);
}